import React, { useState, useRef } from 'react';
import './AddressAutocomplete.css';

const GOOGLE_PLACES_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';

const matchesStateInDescription = (description, stateCode) => {
  const re = new RegExp(`,\\s*${stateCode}(\\s*,|\\s*$)`, 'i');
  return re.test(description);
};

const placesAutocompleteLegacy = async (input) => {
  const url = `/google-places/autocomplete/json?input=${encodeURIComponent(input)}&types=address&components=country:us&key=${GOOGLE_PLACES_KEY}&language=en`;
  const res = await fetch(url);
  const data = await res.json();
  if (!data.predictions) return [];
  return data.predictions.map(p => ({
    placePrediction: {
      placeId: p.place_id,
      text: { text: p.description },
      structuredFormat: {
        mainText: { text: p.structured_formatting?.main_text || p.description },
        secondaryText: { text: p.structured_formatting?.secondary_text || '' },
      },
    },
  }));
};

const placesGetDetailsLegacy = async (placeId) => {
  const url = `/google-places/details/json?place_id=${encodeURIComponent(placeId)}&key=${GOOGLE_PLACES_KEY}&fields=address_component,formatted_address,geometry`;
  const res = await fetch(url);
  const data = await res.json();
  if (data.status !== 'OK' || !data.result) throw new Error(`Legacy Places details failed: ${data.status}`);
  return {
    addressComponents: (data.result.address_components || []).map(c => ({
      longText: c.long_name,
      shortText: c.short_name,
      types: c.types,
    })),
    formattedAddress: data.result.formatted_address || '',
  };
};

const placesAutocomplete = async (input) => {
  try {
    const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_PLACES_KEY,
      },
      body: JSON.stringify({ input, includedRegionCodes: ['us'], languageCode: 'en' }),
    });
    if (!res.ok) throw new Error(`Places API (New) autocomplete failed: ${res.status}`);
    const data = await res.json();
    if (data.error) throw new Error(`Places API (New) error: ${data.error.status}`);
    return data.suggestions || [];
  } catch (err) {
    console.warn('Places API (New) unavailable, falling back to legacy proxy:', err.message);
    return await placesAutocompleteLegacy(input);
  }
};

const placesGetDetails = async (placeId) => {
  try {
    const res = await fetch(
      `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}?languageCode=en`,
      {
        headers: {
          'X-Goog-Api-Key': GOOGLE_PLACES_KEY,
          'X-Goog-FieldMask': 'addressComponents,formattedAddress,location,displayName',
        },
      }
    );
    if (!res.ok) throw new Error(`Places API (New) details failed: ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn('Places API (New) details unavailable, falling back to legacy proxy:', err.message);
    return await placesGetDetailsLegacy(placeId);
  }
};

const parseAddressComponents = (place) => {
  const components = place.addressComponents || [];
  const find = (type) => components.find(c => c.types?.includes(type));
  const streetNumber = find('street_number')?.longText || '';
  const route = find('route')?.longText || '';
  const street = streetNumber ? `${streetNumber} ${route}` : route;
  const locality = find('locality')?.longText || '';
  // Google classifies LA-basin communities (Porter Ranch, Woodland Hills,
  // Sherman Oaks, etc.) as "neighborhood", not "sublocality" — they don't
  // carry sublocality_level_1/sublocality components at all, so those two
  // checks alone never catch them and city silently fell back to locality.
  const sublocality =
    find('sublocality_level_1')?.longText ||
    find('sublocality')?.longText ||
    find('neighborhood')?.longText || '';
  // LA-basin communities are administratively part of the City of Los
  // Angeles, so Google always sets locality to "Los Angeles" and puts the
  // actual neighborhood in sublocality/neighborhood. Prefer that there so
  // search/city fields reflect the specific community instead of every
  // listing in LA. Every other city is unaffected.
  const city =
    (locality === 'Los Angeles' && sublocality) ||
    locality ||
    sublocality ||
    find('administrative_area_level_3')?.longText || '';
  const stateComp = find('administrative_area_level_1');
  const stateCode = stateComp?.shortText || '';
  const zip = find('postal_code')?.longText || '';
  return { street, city, stateCode, zip, formattedAddress: place.formattedAddress || '' };
};

const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  onKeyDown,
  placeholder = 'Enter street address',
  ariaLabel,
  inputClassName = '',
  restrictToStateCode,
  outOfStateErrorMessage,
}) => {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectError, setRejectError] = useState('');
  const debounceRef = useRef(null);
  const normalizedStateCode = restrictToStateCode?.toUpperCase();

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);
    setPredictions([]);
    if (rejectError) setRejectError('');

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text.trim() || text.trim().length < 3) {
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const suggestions = await placesAutocomplete(text);
        const mapped = suggestions.slice(0, 5).map(s => ({
          place_id: s.placePrediction?.placeId || s.placePrediction?.place?.split('/').pop() || '',
          description: s.placePrediction?.text?.text || '',
          structured_formatting: {
            main_text: s.placePrediction?.structuredFormat?.mainText?.text || s.placePrediction?.text?.text || '',
            secondary_text: s.placePrediction?.structuredFormat?.secondaryText?.text || '',
          },
        }));
        const filtered = normalizedStateCode
          ? mapped.filter(p => matchesStateInDescription(p.description, normalizedStateCode))
          : mapped;
        setPredictions(filtered);
      } catch (err) {
        console.error('Places autocomplete error:', err);
        setPredictions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  // Enter should search with whatever text is typed (same as picking a
  // suggestion) and close the dropdown — it shouldn't stay open covering
  // the results underneath. The actual search is triggered by the caller's
  // onKeyDown (already wired to run on Enter); this just also closes the list.
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      setPredictions([]);
    }
    onKeyDown && onKeyDown(e);
  };

  const handleSelect = async (prediction) => {
    const mainText = prediction.structured_formatting?.main_text || prediction.description;
    onChange(mainText);
    setPredictions([]);

    try {
      const detail = await placesGetDetails(prediction.place_id);
      const parsed = parseAddressComponents(detail);
      if (parsed.street) onChange(parsed.street);

      if (normalizedStateCode && parsed.stateCode && parsed.stateCode.toUpperCase() !== normalizedStateCode) {
        setRejectError(
          outOfStateErrorMessage ??
            `Address must be in ${normalizedStateCode}. You can only select properties in your licensed state.`
        );
        onChange('');
        return;
      }

      onAddressSelect(parsed);
    } catch (err) {
      console.error('Place details error:', err);
    }
  };

  return (
    <div className="addr-ac-wrapper">
      <input
        type="text"
        className={inputClassName}
        value={value}
        onChange={handleChange}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
      />
      {loading && <div className="addr-ac-spinner" />}
      {predictions.length > 0 && (
        <div className="addr-ac-dropdown">
          {predictions.map((pred, i) => (
            <button
              key={pred.place_id || i}
              type="button"
              className={`addr-ac-item${i < predictions.length - 1 ? ' addr-ac-item--border' : ''}`}
              onMouseDown={e => e.preventDefault()}
              onClick={() => handleSelect(pred)}
            >
              <svg className="addr-ac-pin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
              </svg>
              <div className="addr-ac-text">
                <span className="addr-ac-main">
                  {pred.structured_formatting?.main_text || pred.description}
                </span>
                {pred.structured_formatting?.secondary_text && (
                  <span className="addr-ac-secondary">
                    {pred.structured_formatting.secondary_text}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      {rejectError && <p className="addr-ac-error">{rejectError}</p>}
    </div>
  );
};

export default AddressAutocomplete;
